import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, Landmark, Search, SlidersHorizontal } from 'lucide-react';
import './JurisprudenceNationale.css';

/* ────────────────────────── Types ────────────────────────── */

interface Partie {
  nom: string;
  qualite: string;
  avocat?: string;
}

interface DecisionNationale {
  id: string;
  numero: string;
  numeroRG: string;
  date: string;
  juridiction: string;
  matiere: string;
  typeDecision: string;
  instance: string;
  objet: string;
  synthese: string;
  parties: Partie[];
  estInterne?: boolean;
}

/* ────────────────────────── Données mock ────────────────────────── */

const DECISIONS: DecisionNationale[] = [
  {
    id: 'nat-1187', numero: '1187/2024', numeroRG: '2024/1187',
    date: '2024-11-14', juridiction: "Tribunal de commerce d'Abidjan",
    matiere: 'Sûretés et garanties', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Appel en garantie sur caution bancaire solidaire",
    synthese: "Le tribunal retient l'engagement de caution solidaire souscrit par la banque et la condamne au paiement du principal réclamé, avec exécution provisoire partielle.",
    parties: [
      { nom: 'SOCIÉTÉ IVOMAT SARL', qualite: 'Demanderesse' },
      { nom: "BANQUE ATLANTIQUE CÔTE D'IVOIRE", qualite: 'Défenderesse' },
    ],
    estInterne: true,
  },
  {
    id: 'nat-412', numero: '412/2025', numeroRG: '2025/0412',
    date: '2025-06-04', juridiction: "Cour d'appel de commerce d'Abidjan",
    matiere: 'Sûretés et garanties', typeDecision: 'Arrêt', instance: 'Appel',
    objet: "Expertise comptable ordonnée avant dire droit",
    synthese: "La cour ordonne une expertise comptable pour déterminer le solde exigible et sursoit à statuer sur le surplus.",
    parties: [
      { nom: "BANQUE ATLANTIQUE CÔTE D'IVOIRE", qualite: 'Appelante' },
      { nom: 'SOCIÉTÉ IVOMAT SARL', qualite: 'Intimée' },
    ],
    estInterne: true,
  },
  {
    id: 'nat-261', numero: '261/2025', numeroRG: '2025/0261',
    date: '2025-03-11', juridiction: 'Tribunal de commerce de Bouaké',
    matiere: 'Recouvrement de créance', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Validité de la mise en demeure et intérêts de retard",
    synthese: "La juridiction confirme le caractère exigible de la créance après une mise en demeure régulièrement adressée.",
    parties: [
      { nom: 'SOCIÉTÉ KANZA DISTRIBUTION', qualite: 'Demanderesse' },
      { nom: 'ETS KOUADIO FRÈRES', qualite: 'Défenderesse' },
    ],
  },
  {
    id: 'nat-97', numero: '97/2026', numeroRG: '2026/0097',
    date: '2026-01-22', juridiction: "Cour d'appel d'Abidjan",
    matiere: 'Droit des sociétés', typeDecision: 'Arrêt', instance: 'Appel',
    objet: "Responsabilité du gérant pour faute de gestion",
    synthese: "La cour retient une faute de gestion caractérisée et confirme l'obligation de réparer le préjudice subi par la société.",
    parties: [
      { nom: 'SOCIÉTÉ IVOIRE LOGISTIQUE', qualite: 'Appelante' },
      { nom: 'M. KOUASSI YAO', qualite: 'Intimé' },
    ],
  },
  {
    id: 'nat-743', numero: '743/2024', numeroRG: '2024/0743',
    date: '2024-09-09', juridiction: 'Tribunal de commerce de San Pedro',
    matiere: 'Contrats commerciaux', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Résiliation d'un contrat de fourniture pour inexécution",
    synthese: "La résiliation est prononcée aux torts du fournisseur, faute de livraisons conformes dans les délais convenus.",
    parties: [
      { nom: 'SOCIÉTÉ PORTUAIRE DE SAN PEDRO', qualite: 'Demanderesse' },
      { nom: 'AFRICA SUPPLY', qualite: 'Défenderesse' },
    ],
  },
  {
    id: 'nat-323', numero: '323/2025', numeroRG: '2025/0323',
    date: '2025-05-26', juridiction: 'Tribunal de commerce de Yamoussoukro',
    matiere: 'Procédures collectives', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Admission de créance au passif d'une liquidation",
    synthese: "La créance déclarée est admise au passif après vérification des pièces justificatives produites.",
    parties: [
      { nom: "SOCIÉTÉ CÔTE D'IVOIRE FINANCE", qualite: 'Demanderesse' },
      { nom: 'SARL BÂTIR PLUS', qualite: 'Défenderesse' },
    ],
  },
  {
    id: 'nat-65', numero: '65/2026', numeroRG: '2026/0065',
    date: '2026-02-13', juridiction: 'Cour suprême',
    matiere: 'Voies de recours', typeDecision: 'Arrêt', instance: 'Cassation',
    objet: "Rejet du pourvoi pour défaut de moyen recevable",
    synthese: "La chambre judiciaire rejette le pourvoi, les griefs soulevés ne satisfaisant pas aux exigences de précision.",
    parties: [
      { nom: "M. KOFFI N'DRI", qualite: 'Demandeur' },
      { nom: 'SOCIÉTÉ AGRO CI', qualite: 'Défenderesse' },
    ],
  },
  {
    id: 'nat-819', numero: '819/2025', numeroRG: '2025/0819',
    date: '2025-10-18', juridiction: "Tribunal de commerce d'Abidjan",
    matiere: 'Baux commerciaux', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Renouvellement du bail commercial et indemnité d'éviction",
    synthese: "Le refus de renouvellement ouvre droit à une indemnité d'éviction calculée selon la valeur du fonds exploité.",
    parties: [
      { nom: "ETS N'DA SERVICES", qualite: 'Demandeur' },
      { nom: 'IMMOBILIÈRE LAGUNE', qualite: 'Défenderesse' },
    ],
    estInterne: true,
  },
  {
    id: 'nat-508', numero: '508/2024', numeroRG: '2024/0508',
    date: '2024-06-21', juridiction: "Cour d'appel de commerce d'Abidjan",
    matiere: 'Recouvrement de créance', typeDecision: 'Arrêt', instance: 'Appel',
    objet: "Confirmation d'une ordonnance d'injonction de payer",
    synthese: "La cour confirme l'ordonnance dès lors que la créance est certaine, liquide et exigible.",
    parties: [
      { nom: 'SOCIÉTÉ ORANGE CI', qualite: 'Appelante' },
      { nom: 'ENTREPRISE KOUADIO BTP', qualite: 'Intimée' },
    ],
    estInterne: true,
  },
  {
    id: 'nat-206', numero: '206/2026', numeroRG: '2026/0206',
    date: '2026-03-02', juridiction: 'Tribunal de commerce de Man',
    matiere: 'Droit bancaire', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Clôture de compte et information du client",
    synthese: "La banque est tenue d'informer son client des modalités de clôture et des opérations restant à régulariser.",
    parties: [
      { nom: 'M. BAKAYOKO SORO', qualite: 'Demandeur' },
      { nom: 'BANQUE RÉGIONALE', qualite: 'Défenderesse' },
    ],
  },
  {
    id: 'nat-331', numero: '331/2024', numeroRG: '2024/0331',
    date: '2024-04-30', juridiction: 'Tribunal de commerce de Daloa',
    matiere: 'Assurances', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Garantie de l'assureur et déclaration du sinistre",
    synthese: "L'assureur doit sa garantie lorsque la déclaration du sinistre intervient dans le délai contractuel.",
    parties: [
      { nom: 'SOCIÉTÉ CACAO PLUS', qualite: 'Demanderesse' },
      { nom: 'ASSURANCES IVOIRE', qualite: 'Défenderesse' },
    ],
  },
];

/* ────────────────────────── Recherche ────────────────────────── */

interface Criteres {
  phrase: string;
  matiere: string;
  type: string;
  juridiction: string;
  dateDebut: string;
  dateFin: string;
  dateExacte: string;
  numeroRG: string;
  numeroDecision: string;
  demandeur: string;
  defendeur: string;
}

const CRITERES_VIDES: Criteres = {
  phrase: '', matiere: '', type: '', juridiction: '',
  dateDebut: '', dateFin: '', dateExacte: '',
  numeroRG: '', numeroDecision: '', demandeur: '', defendeur: '',
};

const MATIERES = Array.from(new Set(DECISIONS.map(d => d.matiere))).sort();
const TYPES = Array.from(new Set(DECISIONS.map(d => d.typeDecision))).sort();
const JURIDICTIONS = Array.from(new Set(DECISIONS.map(d => d.juridiction))).sort();

function normaliser(texte: string): string {
  return texte.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function filtrer(c: Criteres, decisions: DecisionNationale[]): DecisionNationale[] {
  const mots = c.phrase.trim() ? c.phrase.trim().split(/\s+/) : [];
  const rg = normaliser(c.numeroRG.trim());
  const num = normaliser(c.numeroDecision.trim());
  const dem = normaliser(c.demandeur.trim());
  const def = normaliser(c.defendeur.trim());

  return decisions.filter(d => {
    if (c.juridiction && d.juridiction !== c.juridiction) return false;
    if (c.matiere && d.matiere !== c.matiere) return false;
    if (c.type && d.typeDecision !== c.type) return false;
    if (c.dateDebut && d.date < c.dateDebut) return false;
    if (c.dateFin && d.date > c.dateFin) return false;
    if (c.dateExacte && d.date !== c.dateExacte) return false;
    if (rg && !normaliser(d.numeroRG).includes(rg)) return false;
    if (num && !normaliser(d.numero).includes(num)) return false;
    if (dem && !d.parties.some(p => normaliser(p.nom).includes(dem))) return false;
    if (def && !d.parties.some(p => normaliser(p.nom).includes(def))) return false;
    if (mots.length === 0) return true;
    const bloc = normaliser([d.numeroRG, d.numero, d.juridiction, d.matiere, d.objet, d.synthese, ...d.parties.map(p => p.nom)].join(' '));
    return mots.every(m => bloc.includes(normaliser(m)));
  });
}

function formatDateFR(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function extraitCorrespondant(decision: DecisionNationale, phrase: string): string {
  const termes = phrase.trim() ? phrase.trim().split(/\s+/).map(normaliser) : [];
  const passages = [decision.objet, decision.synthese];
  const passage = passages.find(texte =>
    termes.length > 0 && termes.some(terme => normaliser(texte).includes(terme)),
  );
  const texte = passage ?? decision.synthese;
  return texte.length > 190 ? `${texte.slice(0, 187).trimEnd()}\u2026` : texte;
}

/* ────────────────────────── Composant principal ────────────────────────── */

const JurisprudenceNationale: React.FC = () => {
  const [criteres, setCriteres] = useState<Criteres>(CRITERES_VIDES);
  const [recherche, setRecherche] = useState<Criteres | null>(null);
  const [avancee, setAvancee] = useState(false);
  const navigate = useNavigate();

  const resultats = useMemo(
    () => recherche ? filtrer(recherche, DECISIONS) : [],
    [recherche],
  );

  const maj = (cle: keyof Criteres) => (val: string) =>
    setCriteres(c => ({ ...c, [cle]: val }));

  const lancer = () => setRecherche(criteres);
  const reinitialiser = () => { setCriteres(CRITERES_VIDES); setRecherche(null); };

  return (
    <main className="jn-page">
      {/* Hero */}
      <section className="jn-hero">
        <div className="jn-hero-gradient" />
        <div className="jn-hero-content">
          <p className="jn-eyebrow"><Landmark size={18} /> Fonds national</p>
          <h1>Jurisprudence nationale</h1>
          <p className="jn-description">
            Toutes les décisions du fonds national, toutes juridictions confondues, avec les noms des parties.
          </p>

          <form className="jn-search-form" onSubmit={e => { e.preventDefault(); lancer(); }}>
            <div className="jn-search-input">
              <Search size={22} />
              <input
                value={criteres.phrase}
                onChange={e => maj('phrase')(e.target.value)}
                placeholder="Recherchez une décision de justice ou un texte de loi\u2026"
              />
            </div>
            <button type="submit" className="jn-search-btn">Rechercher</button>
          </form>

          <button className="jn-advanced-toggle" onClick={() => setAvancee(v => !v)}>
            <SlidersHorizontal size={16} /> Recherche avancée
          </button>
        </div>
      </section>

      {/* Filtres avancés */}
      {avancee && (
        <div className="jn-advanced">
          <div className="jn-advanced-grid">
            <div>
              <label className="jn-field-label">Période — du</label>
              <input type="date" value={criteres.dateDebut} onChange={e => maj('dateDebut')(e.target.value)} className="jn-field" />
            </div>
            <div>
              <label className="jn-field-label">Période — au</label>
              <input type="date" value={criteres.dateFin} onChange={e => maj('dateFin')(e.target.value)} className="jn-field" />
            </div>
            <div>
              <label className="jn-field-label">Date de la décision</label>
              <input type="date" value={criteres.dateExacte} onChange={e => maj('dateExacte')(e.target.value)} className="jn-field" />
            </div>
            <div>
              <label className="jn-field-label">Numéro RG</label>
              <input value={criteres.numeroRG} onChange={e => maj('numeroRG')(e.target.value)} placeholder="Ex. : 2024/1187" className="jn-field" />
            </div>
            <div>
              <label className="jn-field-label">Numéro de décision</label>
              <input value={criteres.numeroDecision} onChange={e => maj('numeroDecision')(e.target.value)} placeholder="Ex. : 1187/2024" className="jn-field" />
            </div>
            <div>
              <label className="jn-field-label">Matière</label>
              <select value={criteres.matiere} onChange={e => maj('matiere')(e.target.value)} className="jn-field">
                <option value="">Toutes les matières</option>
                {MATIERES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="jn-field-label">Type de décision</label>
              <select value={criteres.type} onChange={e => maj('type')(e.target.value)} className="jn-field">
                <option value="">Tous les types</option>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="jn-field-label">Juridiction</label>
              <select value={criteres.juridiction} onChange={e => maj('juridiction')(e.target.value)} className="jn-field">
                <option value="">Toutes les juridictions</option>
                {JURIDICTIONS.map(j => <option key={j} value={j}>{j}</option>)}
              </select>
            </div>
            <div>
              <label className="jn-field-label">Demandeur</label>
              <input value={criteres.demandeur} onChange={e => maj('demandeur')(e.target.value)} placeholder="Ex. : IVOMAT" className="jn-field" />
            </div>
            <div>
              <label className="jn-field-label">Défendeur</label>
              <input value={criteres.defendeur} onChange={e => maj('defendeur')(e.target.value)} placeholder="Ex. : BANQUE ATLANTIQUE" className="jn-field" />
            </div>
          </div>
          <div className="jn-advanced-actions">
            <button className="jn-reset-btn" onClick={reinitialiser}>Réinitialiser</button>
            <button className="jn-apply-btn" onClick={lancer}>Lancer la recherche</button>
          </div>
        </div>
      )}

      {/* État vide */}
      {!recherche && (
        <p className="jn-empty">Lancez une recherche pour explorer le fonds national de jurisprudence.</p>
      )}

      {/* Résultats */}
      {recherche && (
        <section className="jn-results">
          <p className="jn-results-count">
            {resultats.length} décision{resultats.length > 1 ? 's' : ''} trouvée{resultats.length > 1 ? 's' : ''} — Jurisprudence nationale
            {recherche.matiere ? ` \u00b7 ${recherche.matiere}` : ''}
          </p>

          {resultats.length === 0 && (
            <div className="jn-no-results">
              <FileText size={28} />
              <strong>Aucune décision ne correspond à votre recherche.</strong>
              <span>Essayez un autre mot-clé ou modifiez les filtres avancés.</span>
            </div>
          )}

          <div className="jn-results-list">
            {resultats.map(d => (
              <article key={d.id} className="jn-result-card">
                <div className="jn-result-heading">
                  <span className="jn-doc-icon"><FileText size={18} /></span>
                  <div className="jn-result-title">
                    <h3>Décision n° {d.numero} · {formatDateFR(d.date)}</h3>
                    <p>{d.juridiction} · RG n° {d.numeroRG} · {d.matiere} · {d.instance}</p>
                  </div>
                  <span className="jn-badge-perimetre">
                    {d.estInterne ? 'Votre juridiction' : 'Fonds national'}
                  </span>
                </div>

                <p className="jn-result-objet">{d.objet}</p>
                <p className="jn-result-synthese">{extraitCorrespondant(d, recherche.phrase)}</p>

                <div className="jn-result-footer">
                  <span className="jn-result-parties">{d.parties.map(p => p.nom).join(' · ')}</span>
                  <button className="jn-voir-btn" onClick={() => navigate(`/espace-tca/jurisprudence-nationale/${d.id}`)}>
                    Voir la décision <ArrowRight size={16} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

    </main>
  );
};

export default JurisprudenceNationale;
